/**
 * Composable for handling hierarchical location display logic
 */
interface LocationItem {
    id: number;
    name: string;
    iataCode?: string;
    countryCodeAlpha2?: string;
    icaoCode?: string;
    children?: LocationItem[];
}

interface BuildLocationDisplayOptions {
    includeMetadata?: boolean;
    /**
     * Optional custom display name builder.
     * When provided, it will be used to build the displayName for both
     * top-level items and child items. If `child` is undefined, the builder
     * should return the label for the top-level item. Otherwise, it should
     * return the label for the child item, potentially using information from
     * the top-level item as well.
     *
     * Default behavior replicates the existing implementation:
     *  - top-level: iataCode || name
     *  - child: `${top.iataCode || top.name} · ${child.name}`
     */
    displayNameBuilder?: (top: LocationItem, child?: LocationItem) => string;
}

interface LocationDisplayObject {
    id: string;
    displayName: string;
    countDisplay: string;
    countryCodeAlpha2?: string;
    icaoCode?: string;
}

type SelectionState = 'selected' | 'unselected' | 'indeterminate';

/**
 * Get all descendant IDs from a hierarchical item
 * @param item - The hierarchical item
 * @returns Array of descendant IDs
 */
export function getAllDescendantIds(item: LocationItem): number[] {
    const ids: number[] = [];

    if (item.children) {
        for (const child of item.children) {
            ids.push(child.id);
            ids.push(...getAllDescendantIds(child));
        }
    }
    return ids;
}

/**
 * Get the selection state of a hierarchical item
 * @param item - The hierarchical item
 * @param selectedIds - Array of selected location IDs
 * @returns Selection state
 */
export function getSelectionState(item: LocationItem, selectedIds: number[]): SelectionState {
    const isSelected: boolean = selectedIds.includes(item.id);

    if (!item.children || item.children.length === 0) {
        // Leaf node - just check if it's selected
        return isSelected ? 'selected' : 'unselected';
    }

    // Parent node - check children state
    const descendantIds: number[] = getAllDescendantIds(item);
    const selectedDescendants: number[] = descendantIds.filter((id: number) => selectedIds.includes(id));

    if (selectedDescendants.length === 0) {
        return isSelected ? 'selected' : 'unselected';
    } else if (selectedDescendants.length === descendantIds.length) {
        return 'selected';
    } else {
        return 'indeterminate';
    }
}

/**
 * Build hierarchical location display objects
 * @param hierarchicalOptions - Array of hierarchical location data
 * @param selectedIds - Array of selected location IDs
 * @param options - Configuration options
 * @returns Array of display objects
 */
export function buildLocationDisplayObjects(
    hierarchicalOptions: LocationItem[],
    selectedIds: number[],
    options: BuildLocationDisplayOptions = {},
): LocationDisplayObject[] {
    const { includeMetadata = false, displayNameBuilder }: BuildLocationDisplayOptions = options;

    // Default builder replicates existing behavior
    const defaultBuilder = (top: LocationItem, child?: LocationItem): string => {
        if (!child) {
            return top.iataCode || top.name;
        }
        return `${top.iataCode || top.name} · ${child.name}`;
    };
    const buildName = displayNameBuilder ?? defaultBuilder;

    if (selectedIds.length === 0 || !hierarchicalOptions?.length) {
        return [];
    }

    // "ALL" only makes sense for a node that actually has children — it means
    // every one of them is selected. A selected leaf (e.g. a single maintainer
    // contact, or a single airport) gets no count pill.
    const hasChildren = (item: LocationItem): boolean => Array.isArray(item.children) && item.children.length > 0;

    const result: LocationDisplayObject[] = [];

    for (const topLevel of hierarchicalOptions) {
        const topState: SelectionState = getSelectionState(topLevel, selectedIds);

        if (topState === 'selected') {
            const displayObject: LocationDisplayObject = {
                id: `item-${topLevel.id}`,
                displayName: buildName(topLevel),
                countDisplay: hasChildren(topLevel) ? 'ALL' : '',
            };

            if (includeMetadata) {
                displayObject.countryCodeAlpha2 = topLevel.countryCodeAlpha2;
                displayObject.icaoCode = topLevel.icaoCode;
            }

            result.push(displayObject);
        } else if (topState === 'indeterminate' && topLevel.children) {
            for (const child of topLevel.children) {
                const childState: SelectionState = getSelectionState(child, selectedIds);
                const childDisplayName: string = buildName(topLevel, child);

                if (childState === 'selected') {
                    const displayObject: LocationDisplayObject = {
                        id: `item-${child.id}`,
                        displayName: childDisplayName,
                        countDisplay: hasChildren(child) ? 'ALL' : '',
                    };

                    if (includeMetadata) {
                        displayObject.countryCodeAlpha2 = topLevel.countryCodeAlpha2;
                        displayObject.icaoCode = topLevel.icaoCode;
                    }

                    result.push(displayObject);
                } else if (childState === 'indeterminate') {
                    const descendants: number[] = getAllDescendantIds(child);
                    const selectedCount: number = descendants.filter((id: number) => selectedIds.includes(id)).length;

                    const displayObject: LocationDisplayObject = {
                        id: `item-${child.id}`,
                        displayName: childDisplayName,
                        countDisplay: `${selectedCount}/${descendants.length}`,
                    };

                    if (includeMetadata) {
                        displayObject.countryCodeAlpha2 = topLevel.countryCodeAlpha2;
                        displayObject.icaoCode = topLevel.icaoCode;
                    }

                    result.push(displayObject);
                }
            }
        }
    }

    return result;
}

interface UseHierarchicalLocationDisplayReturn {
    getAllDescendantIds: typeof getAllDescendantIds;
    getSelectionState: typeof getSelectionState;
    buildLocationDisplayObjects: typeof buildLocationDisplayObjects;
}

export function useHierarchicalLocationDisplay(): UseHierarchicalLocationDisplayReturn {
    return {
        getAllDescendantIds,
        getSelectionState,
        buildLocationDisplayObjects,
    };
}
